import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Separator,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { useAuth } from '@/hooks/use-auth';
import {
  usePaymentLinkMerchant,
  useSetPaymentLinkMerchantEnabled,
  useRotatePaymentLinkSalt,
} from '@/hooks/use-payment-link';
import { ApiError } from '@/lib/api/client';
import { formatDate } from '@/lib/utils';
import { ROLE_GROUPS } from '@/types/auth';
import type {
  PaymentLinkMerchantSetResponse,
  PaymentLinkSaltRotationResponse,
} from '@/types/payment-link';
import { SaltRevealDialog } from '@/components/features/payment-link/salt-reveal-dialog';
import { ActivateMerchantConfirm } from '@/components/features/payment-link/activate-merchant-confirm';
import { RotateSaltConfirm } from '@/components/features/payment-link/rotate-salt-confirm';

interface MerchantStatusDrawerProps {
  /** Selected merchant id; the drawer is open iff this is non-null. */
  merchantId: string | null;
  /** Resolved display name (falls back to the raw id upstream). */
  merchantName?: string;
  onOpenChange: (open: boolean) => void;
}

function YesNo({ value }: { value: boolean }) {
  const { t } = useTranslation('payment-link');
  return (
    <Badge variant={value ? 'default' : 'outline'}>
      {value ? t('common.yes') : t('common.no')}
    </Badge>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}

/**
 * Provisionierungs-Status eines Merchants (Spec 493 Inc 1 read + Spec 497 Inc 2 mutations).
 *
 * Fuer platform_admin/platform_it (PAYMENT_LINK_WRITE) erscheinen die Aktionen aktivieren/
 * deaktivieren + Salt rotieren; platform_ops sieht die Flaeche read-only. Der Salt-Klartext
 * kommt AUS DER MUTATIONS-ANTWORT (D-Reveal), nie aus einem nachgelagerten GET; er lebt nur im
 * Component-State und wird beim Schliessen des Reveal-Modals verworfen. `FacadeCredentialProvisioned:
 * false` (explizit) erzeugt einen sichtbaren Warnhinweis (D5). Ein 503 (retrybar) zeigt einen
 * nicht-blockierenden Retry-Hinweis (D-503).
 */
export function MerchantStatusDrawer({
  merchantId,
  merchantName,
  onOpenChange,
}: MerchantStatusDrawerProps) {
  const { t } = useTranslation('payment-link');
  const { hasRole } = useAuth();
  const canWrite = hasRole(ROLE_GROUPS.PAYMENT_LINK_WRITE);
  const { data, isLoading, error, refetch } = usePaymentLinkMerchant(merchantId ?? undefined);

  const setEnabled = useSetPaymentLinkMerchantEnabled();
  const rotateSalt = useRotatePaymentLinkSalt();

  // Which confirm dialog is open, and the once-only salt cleartext to reveal (from the response).
  const [confirm, setConfirm] = useState<'toggle' | 'rotate' | null>(null);
  const [revealSalt, setRevealSalt] = useState<string | null>(null);

  const displayName = merchantName ?? merchantId ?? '';

  function handleMutationError(err: unknown) {
    setConfirm(null);
    if (err instanceof ApiError && err.status === 503) {
      toast.error(t('retry.unavailable'));
      return;
    }
    toast.error(err instanceof Error ? err.message : t('retry.unavailable'));
  }

  function onSetResult(res: PaymentLinkMerchantSetResponse) {
    setConfirm(null);
    if (res.SaltCleartext) {
      // First-activation: reveal the salt exactly once (sourced from THIS response).
      setRevealSalt(res.SaltCleartext);
    } else {
      toast.success(res.Enabled ? t('toast.activated') : t('toast.deactivated'));
    }
    // D5: an explicit false (activated but no Facade credential) is NOT a full success.
    if (res.FacadeCredentialProvisioned === false) {
      toast.error(t('warning.credentialMissing'));
    }
  }

  function onRotateResult(res: PaymentLinkSaltRotationResponse) {
    setConfirm(null);
    setRevealSalt(res.SaltCleartext);
  }

  /** Close the reveal AND drop the mutation result — the cleartext must not linger in the
   *  React-Query mutation cache after the operator has dismissed the modal (M1). */
  function closeReveal() {
    setRevealSalt(null);
    setEnabled.reset();
    rotateSalt.reset();
  }

  function submitToggle() {
    if (!merchantId || !data) return;
    setEnabled.mutate(
      { merchantId, enabled: !data.Enabled },
      { onSuccess: onSetResult, onError: handleMutationError },
    );
  }

  function submitRotate() {
    if (!merchantId) return;
    rotateSalt.mutate(
      { merchantId },
      { onSuccess: onRotateResult, onError: handleMutationError },
    );
  }

  return (
    <>
      <Sheet open={merchantId !== null} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{displayName}</SheetTitle>
            <SheetDescription>{t('drawer.subtitle')}</SheetDescription>
          </SheetHeader>

          {isLoading && (
            <p className="mt-6 text-sm text-muted-foreground">{t('drawer.loading')}</p>
          )}

          {error && (
            <div className="mt-6">
              <ApiErrorDisplay error={error} onRetry={() => refetch()} />
            </div>
          )}

          {data && (
            <div className="mt-6">
              {/* D5: "enabled but no Facade credential" is the persistently-surfaced broken state —
                  derived from the status itself (not just a transient toast), so it stays visible
                  as long as the merchant is in that state. A dienst-supplied Warning is shown
                  otherwise. */}
              {(() => {
                const credentialMissing = data.Enabled && !data.HasFacadeCredential;
                const warnText = credentialMissing ? t('warning.credentialMissing') : data.Warning;
                return warnText ? (
                  <div
                    role="alert"
                    className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <span>{warnText}</span>
                  </div>
                ) : null;
              })()}

              <dl className="divide-y">
                <Field label={t('drawer.fields.merchantId')}>
                  <span className="font-mono text-xs">{data.MerchantId}</span>
                </Field>
                <Field label={t('drawer.fields.enabled')}>
                  <Badge variant={data.Enabled ? 'default' : 'outline'}>
                    {data.Enabled ? t('status.enabled') : t('status.disabled')}
                  </Badge>
                </Field>
                <Field label={t('drawer.fields.hasSalt')}>
                  <YesNo value={data.HasSalt} />
                </Field>
                <Field label={t('drawer.fields.saltRotationCount')}>
                  <span className="tabular-nums">{data.SaltRotationCount}</span>
                </Field>
                <Field label={t('drawer.fields.hasFacadeCredential')}>
                  <YesNo value={data.HasFacadeCredential} />
                </Field>
                <Field label={t('drawer.fields.credentialRotationCount')}>
                  <span className="tabular-nums">{data.CredentialRotationCount}</span>
                </Field>
                <Field label={t('drawer.fields.serviceCount')}>
                  <span className="tabular-nums">{data.ServiceCount}</span>
                </Field>
                <Field label={t('drawer.fields.createdAt')}>
                  {data.CreatedAt ? formatDate(data.CreatedAt) : t('common.notSet')}
                </Field>
                <Field label={t('drawer.fields.enabledAt')}>
                  {data.EnabledAt ? formatDate(data.EnabledAt) : t('common.notSet')}
                </Field>
              </dl>

              <Separator className="my-4" />

              {canWrite ? (
                <div className="flex flex-col gap-2">
                  <Button
                    variant={data.Enabled ? 'outline' : 'default'}
                    onClick={() => setConfirm('toggle')}
                    disabled={setEnabled.isPending || rotateSalt.isPending}
                  >
                    {data.Enabled ? t('actions.deactivate') : t('actions.activate')}
                  </Button>
                  {data.HasSalt && (
                    <Button
                      variant="outline"
                      onClick={() => setConfirm('rotate')}
                      disabled={setEnabled.isPending || rotateSalt.isPending}
                    >
                      {t('actions.rotateSalt')}
                    </Button>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">{t('drawer.readOnlyNote')}</p>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {data && (
        <ActivateMerchantConfirm
          open={confirm === 'toggle'}
          onOpenChange={(open) => !open && setConfirm(null)}
          merchantName={displayName}
          enabled={!data.Enabled}
          onConfirm={submitToggle}
          isPending={setEnabled.isPending}
        />
      )}

      <RotateSaltConfirm
        open={confirm === 'rotate'}
        onOpenChange={(open) => !open && setConfirm(null)}
        merchantName={displayName}
        onConfirm={submitRotate}
        isPending={rotateSalt.isPending}
      />

      {revealSalt !== null && (
        <SaltRevealDialog
          open
          onOpenChange={(open) => !open && closeReveal()}
          saltCleartext={revealSalt}
          onConfirm={closeReveal}
        />
      )}
    </>
  );
}
